// app/src/lib/label.ts — shelf and product labels for the thermal printer.
//
// Item 9 of the nine-part UI plan. The counter had exactly one thing it could
// print: a customer receipt. Products carry a barcode and the till has a
// scanner, but nothing produced the label that closes that loop — so a new part
// went on the shelf with a handwritten price or nothing at all, and scanning it
// at the till was impossible.
//
// Same 80mm roll as the receipt, so no second printer and no new stationery.
// The barcode is drawn as bars rather than fetched as an image, because the
// print agent is on-site and may have no internet at the moment someone needs a
// label — and a label that sometimes arrives without its barcode is worse than
// no label.

export interface LabelData {
  name: string;
  variant?: string | null;
  sku: string;
  /** EAN-13 or any Code-128-able string. Blank prints the label without bars. */
  barcode?: string | null;
  priceText: string;
  /** Shown small under the price, e.g. "inc. VAT" or a fitment note. */
  note?: string | null;
}

const esc = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/* ── Code 128-B ───────────────────────────────────────────────────────────
   Chosen over EAN-13 because it takes any length and any of the printable
   ASCII range, and the SKUs here are "SS-PART-037" — not 13 digits. A real
   EAN-13 also demands a valid check digit, and a barcode that scans as the
   wrong product is worse than one that does not scan at all.                */

const CODE128 = [
  "212222","222122","222221","121223","121322","131222","122213","122312","132212","221213",
  "221312","231212","112232","122132","122231","113222","123122","123221","223211","221132",
  "221231","213212","223112","312131","311222","321122","321221","312212","322112","322211",
  "212123","212321","232121","111323","131123","131321","112313","132113","132311","211313",
  "231113","231311","112133","112331","132131","113123","113321","133121","313121","211331",
  "231131","213113","213311","213131","311123","311321","331121","312113","312311","332111",
  "314111","221411","431111","111224","111422","121124","121421","141122","141221","112214",
  "112412","122114","122411","142112","142211","241211","221114","413111","241112","134111",
  "111242","121142","121241","114212","124112","124211","411212","421112","421211","212141",
  "214121","412121","111143","111341","131141","114113","114311","411113","411311","113141",
  "114131","311141","411131","211412","211214","211232","2331112",
];

/** Bar widths for a Code 128-B encoding of `text`, as an SVG path.
 *  Exported as code128Svg below so the shelf label and the till receipt share
 *  ONE encoder — two implementations of a barcode is two chances to print
 *  something that scans as the wrong thing. */
function code128(text: string, height: number): { svg: string; width: number } | null {
  const vals: number[] = [104];                       // START B
  for (const ch of text) {
    const c = ch.charCodeAt(0);
    if (c < 32 || c > 126) return null;               // outside 128-B
    vals.push(c - 32);
  }
  let sum = vals[0];
  vals.slice(1).forEach((v, i) => { sum += v * (i + 1); });
  vals.push(sum % 103, 106);                          // check digit, STOP

  const UNIT = 1.6;
  let x = 0, d = "";
  for (const v of vals) {
    const pattern = CODE128[v];
    let bar = true;
    for (const w of pattern) {
      const width = Number(w) * UNIT;
      if (bar) d += `M${x.toFixed(2)} 0h${width.toFixed(2)}v${height}h-${width.toFixed(2)}z`;
      x += width;
      bar = !bar;
    }
  }
  return { svg: `<svg width="${x.toFixed(0)}" height="${height}" viewBox="0 0 ${x.toFixed(2)} ${height}" xmlns="http://www.w3.org/2000/svg"><path d="${d}" fill="#000"/></svg>`, width: x };
}

/** One or more labels on an 80mm roll, ready for printHtml(). */
export function renderLabelsHtml(items: LabelData[], widthMm = 80): string {
  const body = items.map((it) => {
    const bars = it.barcode?.trim() ? code128(it.barcode.trim(), 42) : null;
    return `<div class="lab">
      <div class="nm">${esc(it.name)}${it.variant ? ` <span class="vr">${esc(it.variant)}</span>` : ""}</div>
      <div class="pr">${esc(it.priceText)}</div>
      ${it.note ? `<div class="nt">${esc(it.note)}</div>` : ""}
      ${bars ? `<div class="bc">${bars.svg}</div>` : ""}
      <div class="sk">${esc(it.barcode?.trim() || it.sku)}</div>
    </div>`;
  }).join("");

  return `<!doctype html><html><head><meta charset="utf-8"><title>Labels</title><style>
    @page { size: ${widthMm}mm auto; margin: 0; }
    * { box-sizing: border-box; }
    body { margin: 0; width: ${widthMm}mm; font-family: -apple-system, "Segoe UI", system-ui, sans-serif; color: #000; background: #fff; }
    /* Black on white and nothing else. Thermal paper has no colour, and a grey
       that looks fine on screen prints as a smudge. */
    .lab { padding: 5mm 4mm 4mm; border-bottom: 1px dashed #999; page-break-inside: avoid; text-align: center; }
    .lab:last-child { border-bottom: 0; }
    .nm { font-size: 13pt; font-weight: 700; line-height: 1.2; }
    .vr { font-weight: 400; }
    .pr { font-size: 22pt; font-weight: 800; margin-top: 2mm; letter-spacing: -.01em; }
    .nt { font-size: 8pt; margin-top: .5mm; }
    .bc { margin-top: 3mm; }
    .bc svg { max-width: 100%; height: auto; }
    .sk { font-family: "Courier New", monospace; font-size: 8.5pt; letter-spacing: .12em; margin-top: 1mm; }
  </style></head><body>${body}</body></html>`;
}

/** Code 128-B barcode as a standalone SVG string, or empty when the text
 *  cannot be encoded. Callers get "" rather than a broken image. */
export function code128Svg(text: string, height = 40): string {
  const t = String(text ?? "").trim();
  // An empty string still encodes — START, check digit, STOP — so it draws a
  // valid barcode that means nothing. On a receipt that is worse than a blank
  // space: it looks scannable and is not.
  if (!t) return "";
  const r = code128(t, height);
  return r ? r.svg : "";
}
